import { formatPrice } from '../render/legend.ts';

/**
 * Imperial / metric display and input (docs/spec/09-measured-lengths-and-units.md §2).
 * Storage is always imperial (feet, inches, price per foot); only input and display convert.
 * 1 ft = 0.3048 m and 1 in = 2.54 cm exactly. Rounding is JavaScript Math.round (half toward
 * +∞) and toFixed on the binary double.
 */
export type Units = 'imperial' | 'metric';

export const M_PER_FT = 0.3048;
export const CM_PER_IN = 2.54;

export const ftToM = (ft: number) => ft * M_PER_FT;
export const mToFt = (m: number) => m / M_PER_FT;
export const inToCm = (inches: number) => inches * CM_PER_IN;
export const cmToIn = (cm: number) => cm / CM_PER_IN;

/** Regions whose default is imperial; every other region defaults to metric. */
export const IMPERIAL_REGIONS = ['US', 'LR', 'MM'];

/**
 * Default units for a device region: a 2-letter region code ("US") or a locale identifier
 * whose region subtag is taken ("en-US", "en_US", "es-419-x" → no 2-letter region → metric).
 * Unknown / missing → metric.
 */
export function defaultUnitsForRegion(region: string | null | undefined): Units {
  if (!region) return 'metric';
  const parts = region.split(/[-_]/);
  const code = parts.length === 1 ? parts[0] : parts.slice(1).find((p) => /^[A-Za-z]{2}$/.test(p));
  return code && IMPERIAL_REGIONS.includes(code.toUpperCase()) ? 'imperial' : 'metric';
}

const round1 = (v: number) => Math.round(v * 10) / 10;

/**
 * Run / line length. Imperial: integer feet ("82 ft"). Metric: metres to 1 decimal below 100 m
 * ("25.0 m"), integer from 100 m — decided on the value rounded to 0.1 m (99.96 m → "100 m").
 * `approx` prefixes "≈" (photo estimates; measured lengths have none).
 */
export function formatLength(ft: number, units: Units, opts: { approx?: boolean } = {}): string {
  const prefix = opts.approx ? '≈' : '';
  if (units === 'imperial') return `${prefix}${Math.round(ft)} ft`;
  const m = ftToM(ft);
  const r = round1(m);
  return r >= 100 ? `${prefix}${Math.round(m)} m` : `${prefix}${r.toFixed(1)} m`;
}

/** LED spacing / pair spread: integer inches ("12 in") or integer centimetres ("30 cm"). */
export function formatSpacing(inches: number, units: Units): string {
  return units === 'imperial' ? `${Math.round(inches)} in` : `${Math.round(inToCm(inches))} cm`;
}

/** Wash length: feet with 1 decimal only if fractional after rounding to 0.1 ("4 ft", "4.5 ft"), or metres to 1 decimal ("1.2 m"). */
export function formatWash(ft: number, units: Units): string {
  if (units === 'imperial') {
    const r = round1(ft);
    return Number.isInteger(r) ? `${r} ft` : `${r.toFixed(1)} ft`;
  }
  return `${round1(ftToM(ft)).toFixed(1)} m`;
}

/** Photo scale / frame width: integer feet ("40 ft") or metres to 1 decimal ("12.2 m"). */
export function formatFrameWidth(ft: number, units: Units): string {
  return units === 'imperial' ? `${Math.round(ft)} ft` : `${round1(ftToM(ft)).toFixed(1)} m`;
}

/** Price per foot as shown in the chosen units: "$28/ft" or "$91.86/m" (formatPrice on pricePerFt / 0.3048). */
export function formatPricePerUnit(pricePerFt: number, currency: string, units: Units): string {
  return `${formatPrice(pricePerUnitValue(pricePerFt, units), currency)}/${units === 'imperial' ? 'ft' : 'm'}`;
}

/** The stored price per foot as a number in the chosen units (per ft or per m). */
export function pricePerUnitValue(pricePerFt: number, units: Units): number {
  return units === 'imperial' ? pricePerFt : pricePerFt / M_PER_FT;
}

/** A price typed in the chosen units → stored price per foot, full precision ($90/m → 27.432). */
export function pricePerFtFromInput(value: number, units: Units): number {
  return units === 'imperial' ? value : value * M_PER_FT;
}

const LENGTH_RE = /^([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?)\s*(ft|in|cm|m|'|"|′|″|’|”)?$/i;

/**
 * Parses a typed length to FEET, or null. Accepts a number (first "," read as ".") with an
 * optional unit suffix: ft or ' (also ′ ’), in or " (also ″ ”), m, cm (case-insensitive,
 * optional space). No suffix = the main unit of `units` (ft or m). Returns null for anything
 * else (including compound 12'6", trailing text, non-finite values) and for values ≤ 0.
 */
export function parseLength(text: string, units: Units): number | null {
  const m = LENGTH_RE.exec(text.trim().replace(',', '.'));
  if (!m) return null;
  const v = Number(m[1]);
  if (!Number.isFinite(v) || v <= 0) return null;
  const unit = (m[2] ?? (units === 'imperial' ? 'ft' : 'm')).toLowerCase();
  switch (unit) {
    case 'ft': case "'": case '′': case '’': return v;
    case 'in': case '"': case '″': case '”': return v / 12;
    case 'm': return mToFt(v);
    case 'cm': return mToFt(v / 100);
  }
  return null;
}
